/**
 * Contacts Service
 * Centralised service for all contact-related operations
 */

import { getErrorMessage } from '@xanectech/lib';
import { supabase } from '@xanectech/supabase-client/supabase-client';
import { Contact, Contacts } from '@xanectech/types';
import { createLogger } from '@xanectech/lib';
import { get } from 'node:http';

const logger = createLogger('ContactsService');

export class ContactsService {
  /**
   * Fetch all contacts from the database
   * @returns Array of contacts
   */
  static async getAllContacts(): Promise<Contacts> {
    const { data, error, count } = await supabase
      .from('contacts')
      .select('*', { count: 'exact' });

    if (error) {
      throw new Error(`Error fetching contacts: ${error.message}`);
    }

    return { contacts: data as Contact[], count: count ?? 0 };
  }

  /**
   * Add a new contact to the database
   * @param contact Contact data with required email and name
   * @returns The newly created contact
   */
  static async createContact(
    contact: Omit<Partial<Contact>, 'email' | 'name'> &
      Pick<Contact, 'email' | 'name'>,
  ): Promise<Contact> {
    const { data, error } = await supabase
      .from('contacts')
      .insert([contact])
      .single();

    if (error) {
      logger.error('Error creating contact:', {
        error: getErrorMessage(
          error,
          'An error occurred while creating the contact. Please try again later.',
        ),
      });
      throw new Error(`Error adding contact`);
    }

    return data as Contact;
  }
}
